import { Box, Skeleton, SimpleGrid, useToast } from '@chakra-ui/react';
import _ from 'lodash';
import React, { useState, useEffect, useCallback } from 'react';

import {
  date as commonDate,
  typeguards as commonTypeguards,
  utils as commonUtils,
} from '@finley-technologies/common';

import { FundingRequestStatusEnumGQLType } from 'types/dashboard-core-gqlTypes.generated';

import { FilterDatePicker } from 'packages/common-frontend/components/FilterDatePicker';
import { IsolatedMultiSelect } from 'packages/common-frontend/components/MultiSelect';
import {
  PaginationControl,
  usePaginationLimit,
} from 'packages/common-frontend/components/PaginationControl';
import { errorToastConfig } from 'packages/common-frontend/constants';
import { useCreditFacilityContext } from 'packages/credit-facility/hooks/CreditFacilityProvider';
import { useGetFundingRequestsWithEventDetailsLazyQueryGQLType } from 'packages/funding-requests/requests/requests.gql.generated';
import { HistoryTable } from 'packages/funding-requests/ui/history/HistoryTable';
import { statusConfigs } from 'packages/funding-requests/ui/shared/StatusBadge';

const { transformToStartOfDay, transformToEndOfDay } = commonDate;

export const FundingRequestHistoryViewContainer = ({
  allowedStatuses,
  activeCreditFacilities,
}: {
  allowedStatuses: FundingRequestStatusEnumGQLType[];
  activeCreditFacilities?: { id: string; slug: string }[];
}) => {
  const toast = useToast();

  const { creditFacilityId } = useCreditFacilityContext();
  const allStatuses: FundingRequestStatusEnumGQLType[] = _.intersection<FundingRequestStatusEnumGQLType>(
    commonTypeguards.getKeys(statusConfigs),
    allowedStatuses,
  );
  const [selectedStatuses, setSelectedStatuses] = useState<
    FundingRequestStatusEnumGQLType[]
  >(allStatuses);
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(new Date());
  const [creditFacilityIds, setCreditFacilityIds] = useState<string[]>(
    activeCreditFacilities
      ? _.map(activeCreditFacilities, 'id')
      : [creditFacilityId],
  );

  useEffect(() => {
    if (!activeCreditFacilities) {
      setCreditFacilityIds((oldArray) => {
        const newArray = [creditFacilityId];
        return _.isEqual(oldArray, newArray) ? oldArray : newArray;
      });
    }
  }, [creditFacilityId, activeCreditFacilities]);

  const { pageLimit, setPageLimit, options } = usePaginationLimit(5, [
    5,
    10,
    25,
  ]);

  const [
    getFundingRequests,
    { loading: fundingRequestHistoryLoading, data: fundingRequestHistoryData },
  ] = useGetFundingRequestsWithEventDetailsLazyQueryGQLType({
    /**
     *  Programmatically added onError handler.
     * @onError-TODO: Explicitly handle errors for this fetch
     */
    onError: (err) => {
      errorToastConfig.title = commonUtils.EnvUtils.isProduction()
        ? 'An unexpected error occurred. Please contact Finley for support.'
        : err.message;
      toast(errorToastConfig);
    },
    fetchPolicy: 'network-only',
  });

  const fetchFundingRequests = useCallback(
    (cursor?: { after: string } | { before: string }) => {
      return getFundingRequests({
        variables: {
          limit: pageLimit,
          creditFacilityIds,
          ...cursor,
          startDate: startDate
            ? transformToStartOfDay(startDate).getTime()
            : null,
          endDate: endDate ? transformToEndOfDay(endDate).getTime() : null,
          statuses: selectedStatuses,
        },
      });
    },
    [
      creditFacilityIds,
      endDate,
      getFundingRequests,
      pageLimit,
      selectedStatuses,
      startDate,
    ],
  );

  useEffect(() => {
    fetchFundingRequests();
  }, [
    startDate,
    endDate,
    pageLimit,
    creditFacilityIds,
    getFundingRequests,
    selectedStatuses,
    fetchFundingRequests,
  ]);

  if (!fundingRequestHistoryData || fundingRequestHistoryLoading) {
    return (
      <Skeleton height="600px" data-testid="funding-request-history-loading" />
    );
  }

  const {
    fundingRequests: {
      nodes: fundingRequests,
      pageInfo: { hasNextPage, hasPreviousPage, startCursor, endCursor },
    },
  } = fundingRequestHistoryData;

  const fundingRequestStatusOptions = _.map(allStatuses, (status) => {
    return {
      value: status,
      label: statusConfigs[status].label,
    };
  });

  return (
    <Box height="100%" width="100%">
      <PaginationControl
        filters={
          <SimpleGrid
            columns={{
              sm: 1,
              md: 2,
              lg: 2,
              xl: 4,
            }}
            spacingX={8}
            spacingY={8}
          >
            {activeCreditFacilities && (
              <Box>
                <IsolatedMultiSelect
                  options={_.map(activeCreditFacilities, ({ id, slug }) => ({
                    label: commonUtils.creditFacilitySlugToName(slug),
                    value: id,
                  }))}
                  initialSelections={creditFacilityIds}
                  onApplyOptions={setCreditFacilityIds}
                  optionsHeader="Credit Facilities"
                  titleText={
                    creditFacilityIds.length > 0
                      ? `${creditFacilityIds.length} selected credit facilit${
                          creditFacilityIds.length === 1 ? 'y' : 'ies'
                        }`
                      : 'No credit facilities selected'
                  }
                  applyButtonText="Apply filters"
                />
              </Box>
            )}
            <Box>
              <IsolatedMultiSelect<FundingRequestStatusEnumGQLType>
                options={fundingRequestStatusOptions}
                onApplyOptions={(statuses) => setSelectedStatuses(statuses)}
                initialSelections={selectedStatuses}
                optionsHeader="Status"
                titleText="Select statuses"
                applyButtonText="Apply filter"
              />
            </Box>
            <Box>
              <FilterDatePicker
                startDate={startDate}
                endDate={endDate}
                setStartDate={setStartDate}
                setEndDate={setEndDate}
              />
            </Box>
          </SimpleGrid>
        }
        pageLimit={pageLimit}
        setPageLimit={setPageLimit}
        options={options}
        limitLabel="Items Per Page"
        hasPreviousPage={hasPreviousPage}
        previousLabel="Newer"
        onPrevious={() =>
          fetchFundingRequests({ after: startCursor as string })
        }
        nextLabel="Older"
        hasNextPage={hasNextPage}
        onNext={() => fetchFundingRequests({ before: endCursor as string })}
      >
        <>
          <Box height="20px" />
          <HistoryTable fundingRequests={fundingRequests} />
        </>
      </PaginationControl>
    </Box>
  );
};
